import { IPayment, TPaymentMethod } from "../common/interfaces";

/**
 * Métodos donde el cliente entrega un billete y hay que devolverle cambio.
 *
 * `vales` entra aquí porque el corte de caja lee `cash_received` para esa columna
 * (ReportController, corte de caja), aunque en el mostrador no se dé cambio por vales.
 */
const RECEIVES_CASH: TPaymentMethod[] = ["cash", "vales", "usd"];

/** Métodos que nacen sin aprobar porque los tiene que autorizar un supervisor. */
const NEEDS_AUTHORIZATION: TPaymentMethod[] = ["transfer", "credit"];

interface PaymentLineInput {
  /** Método seleccionado en la botonera del modal de cobro. */
  paymentMethod: TPaymentMethod;
  /** Lo tecleado en "Monto a Pagar", en MXN (para dólares, ya convertido). */
  typedAmount: number;
  /** Lo tecleado cuando el método es dólares, en USD. */
  typedAmountUsd?: number;
  /** Saldo que le falta a la venta: total menos los pagos ya agregados. */
  remaining: number;
  authCode?: string;
  exchangeRate?: number;
  isAnticipo?: boolean;
}

/**
 * Renglón de pago tal como viaja al backend.
 *
 * `amount` es lo que cubre de la venta y `cash_received` lo que puso el cliente en el
 * mostrador; son dos cosas distintas y el ticket depende de que lo sean. El backend
 * imprime el cambio como `cash_received - amount` (ReportController::buildSaleTicketSegments),
 * asi que guardar los dos iguales —que es lo que hacia `addPayment` de payOrder— imprime
 * CAMBIO $0.00 aunque la pantalla haya mostrado el cambio correcto. De paso inflaba el
 * efectivo esperado del corte de caja, que suma `amount` para efectivo.
 *
 * Topar contra el saldo (y no contra el total de la venta) es lo que mantiene sano el pago
 * mixto: el segundo renglon solo puede cubrir lo que quedaba debiendo.
 */
export const buildPaymentLine = ({
  paymentMethod,
  typedAmount,
  typedAmountUsd = 0,
  remaining,
  authCode = "",
  exchangeRate = 0,
  isAnticipo,
}: PaymentLineInput): IPayment => {
  const typed = Number(typedAmount.toFixed(2));
  const saldo = Number(remaining.toFixed(2));

  const payment: IPayment = {
    amount: typed > saldo ? saldo : typed,
    payment_method: paymentMethod,
    cash_received: RECEIVES_CASH.includes(paymentMethod) ? typed : 0,
    auth_code: authCode,
    usd: typedAmountUsd,
    exchange_rate: exchangeRate,
    status: NEEDS_AUTHORIZATION.includes(paymentMethod) ? "pending" : "approved",
  };

  if (isAnticipo) {
    payment.is_anticipo = true;
  }

  return payment;
};

/** Cambio que le toca al cliente por un renglon de pago, tal como lo imprime el ticket. */
export const getPaymentChange = (payment: IPayment): number =>
  Number(((payment.cash_received ?? 0) - payment.amount).toFixed(2));
